import { MinzeElement, Reactive } from 'minze'
import { Locale } from '../../../../modules/locale'
import MainModule from '../../../../modules/main'
import { IFacetOption, IResult, ISearchConfig, MultipleResults } from '../../../../modules/search'
import { SearchResultsSubscriber } from '../../../../modules/search/SearchResultsSubscriber'
import { convertUnixTimestamp } from '../../../../modules/utils'
import { audiences, facetToOption } from '../lvc-audiences-search'
import { LvcRegionSubscriber } from '../lvc-region-search'
import { getCustomPartnerId } from '../../../../modules/routing'
import axios from 'redaxios'
import { config } from '../../../../constants'

const INDEX_LIST_TO_TOTAL = ['events_search', 'exhibitions_search', 'eventseries_search']

Locale.getInstance().registerKeySet('fr', 'lvc-search-results-text', {
  results:
    '{{totalResults}} résultats pour : {{query}} | {{location}} | {{radius}} | {{disciplines}} | {{audience}} | {{dates}}',
  resultsDefault: '{{totalResults}} résultats pour votre recherche',
  radius: 'dans un rayon de {{radius}} km',
})
Locale.getInstance().registerKeySet('en', 'lvc-search-results-text', {
  results:
    '{{totalResults}} results for : {{query}} | {{location}} | {{radius}} | {{disciplines}} | {{audience}} | {{dates}}',
  resultsDefault: '{{totalResults}} results for your search',
  radius: 'within a radius of {{radius}} km',
})

export interface LvcSearchResultsText {
  totalResults: number
  activeSearchFacets: {
    query: string
    location: string
    audience: string
    disciplines: IFacetOption[]
    dates: string[] | string
    radius: number
  }
  city: string
}

const formatMessage = (message: string) => {
  const [beforeResults, afterResults] = message.split(' : ')
  const afterCleaned = afterResults
    .split(' | ')
    .filter(item => item !== '')
    .join(', ')
  return `${beforeResults} : ${afterCleaned}`
}

const getResultsMessage = (data: {
  totalResults: number
  query: string
  location: string
  audience: string
  disciplines: IFacetOption[]
  dates: string[] | string
  radius: number
}) => {
  // concatenate disciplines from the extraData field, which contains the label
  const disciplinesString = data.disciplines
    ? data.disciplines.map(discipline => discipline.extraData).join(', ')
    : ''

  let dateValuesString = ''
  // if dates is an array, check if the 2 dates are the same,
  // if they are the same, return the first one only.
  // if the dates is a string, return the string
  if (Array.isArray(data.dates) && data.dates.length > 0) {
    // Check if the first two dates are the same
    dateValuesString = data.dates[0] === data.dates[1] ? data.dates[0] : data.dates.join(' - ')
  } else if (typeof data.dates === 'string' && data.dates !== '') {
    // If dates is a string, return the string
    dateValuesString = data.dates
  }

  // we concatenate the radius amount with the radius message
  // if it's null, it means the user selected a region, so we don't display the radius
  let radiusMessage = ''
  if (data.radius) {
    radiusMessage = Locale.getKey('lvc-search-results-text.radius', {
      radius: data.radius / 1000, // convert meters to kilometers
    })
  }

  return Locale.getKey('lvc-search-results-text.results', {
    totalResults: data.totalResults,
    query: data.query,
    location: data.location,
    radius: radiusMessage,
    audience: data.audience,
    disciplines: disciplinesString,
    dates: dateValuesString,
  }) as string
}

const messageHandler = (data: {
  totalResults: number
  query: string
  location: string
  audience: string
  disciplines: IFacetOption[]
  dates: string[] | string
  radius: number
}) => {
  // if all the search facets are empty, return the default message
  if (
    data.query === '' &&
    data.location === '' &&
    data.audience === '' &&
    data.disciplines.length === 0 &&
    (data.dates.length === 0 || data.dates === '')
  ) {
    return Locale.getKey('lvc-search-results-text.resultsDefault', {
      totalResults: data.totalResults,
    })
  }
  // if one of the search facets is not empty, return the message with the search facets
  const message = getResultsMessage(data)
  return formatMessage(message)
}

const audienceValuesToLabels = (items: any[]) => {
  return removeAudienceLabelDuplicates(
    items.map(item => {
      return audiences.find(audience => audience.id === facetToOption(item))?.label
    })
  ).join(', ')
}

const removeAudienceLabelDuplicates = (audienceLabels: string[]) => {
  return [...new Set(audienceLabels)]
}

export class LvcSearchResultsText extends MinzeElement {
  private currentSearchConfig: ISearchConfig | null = null
  private searchResultText: string = ''
  searchResultsTextSubscriber: SearchResultsSubscriber | null = null
  lvcRegionSearchTextSubscriber: LvcRegionSubscriber | null = null
  reactive: Reactive = [
    ['totalResults', 0],
    ['city', ''],
  ]
  onReady = async () => {
    this.searchResultsTextSubscriber = new SearchResultsSubscriber(
      this.dataGroupId as string,
      this.onResultsUpdateCallback,
      this.onSearchConfigUpdateCallback
    )
    this.lvcRegionSearchTextSubscriber = new LvcRegionSubscriber(
      this.dataGroupId as string,
      this.onGeoLocationUpdate
    )
    await MainModule.getInstance().getSearchManager().subscribe(this.searchResultsTextSubscriber)
    await MainModule.getInstance().getSearchManager().subscribe(this.lvcRegionSearchTextSubscriber)
  }
  onResultsUpdateCallback = async (results: IResult | MultipleResults) => {
    if (!results) {
      return
    }
    let multiResults = results as MultipleResults
    this.totalResults = 0
    for (const key in multiResults) {
      if (INDEX_LIST_TO_TOTAL.includes(key)) {
        this.totalResults += multiResults[key].total
      }
    }

    this.searchResultText = await this.getSearchResultText()
    this.rerender()
  }

  async getSearchResultText(): Promise<string> {
    // Use customPartnerId from searchConfig, or fall back to URL parameter
    const customPartnerId = this.currentSearchConfig?.customPartnerId || getCustomPartnerId()

    if (customPartnerId) {
      const customPartnerIdText = await this.getCustomPartnerIdData(customPartnerId)
      return `${this.totalResults} ${customPartnerIdText}`
    }

    return messageHandler({ totalResults: this.totalResults, ...this.activeSearchFacets })
  }

  async getCustomPartnerIdData(customPartnerId: string): Promise<string> {
    const lang = Locale.getInstance().getLanguage()
    const resp = await axios.get(`${config.backendApi}/content/getCustomPartnerAttributes`, {
      params: { customPartnerId, lang },
    })
    return resp.data.partnerText
  }

  onSearchConfigUpdateCallback = (searchConfig: ISearchConfig) => {
    // Store the current search config for use in getSearchResultText
    this.currentSearchConfig = searchConfig

    const query = searchConfig.query
    const location = searchConfig.facets?.find(facet => facet.name === 'touristRegion')
      ?.options?.[0]?.value
    const audienceValues = searchConfig.facets?.find(facet => facet.name === 'audience')?.options
    const disciplineValues = searchConfig.facets?.find(
      facet => facet.name === 'uiAdditionalTypesCode'
    )?.options

    let dateCalendar = '' // if the user selected a date from the "flexible" calendar (ex : next 30 days)
    if (searchConfig.dateAnalyticsValue && searchConfig.dateAnalyticsValue.includes('calendrier')) {
      //remove the "calendrier" prefix
      dateCalendar = searchConfig.dateAnalyticsValue.replace('calendrier:', '')
    }

    // these are the dates selected from the date picker
    const dateValues = searchConfig.dateFilters
    const dateValuesString = dateValues?.map(dateValue => {
      return convertUnixTimestamp(dateValue, false)
    })

    const radius = searchConfig?.options?.aroundRadius.events_search || 10000 //default radius is 10km

    this.activeSearchFacets = {
      query: query || '',
      location: location || this.city || '',
      audience: audienceValues ? audienceValuesToLabels(audienceValues) : '',
      disciplines: disciplineValues || [],
      dates: dateCalendar ? dateCalendar : dateValuesString || [],
      radius: !location && this.city ? radius : null, // if the user selected a region, the radius is null
    }
  }
  // On search results update, update the total results and facets
  // Special case for "geolocation" change event
  onGeoLocationUpdate = (data: any, searchMode: any) => {
    if (searchMode === 'regions') {
      this.city = data?.name ?? ''
    }
  }

  html = () => `
  ${
    this.totalResults >= 0
      ? /*html*/ `
    <div class="padding-global">
      <div class="container-large">
        <div class="lvc-search-results-text__wrapper">
          <h2>
            ${this.searchResultText}   
          </h2>
          <lvc-share></lvc-share>
        </div>
      </div>
    </div>
    `
      : ''
  }
  `
  css = () => `
    :host {
      padding-top: 6rem;
    }
    .lvc-search-results-text__wrapper {
      display: flex;
      gap: 2rem;
    }
    lvc-share{
      margin-top: 0.5rem;
      margin-left: auto;
    }
    h2 {
      font-size: 40px;
      font-weight: 800;
      margin:0;
      line-height: 1.1;
      max-width: 40rem;
    }
    .container-large {
      max-width: 80rem;
      margin: 0 auto;
      width: 100%;
    }
    .padding-global {
      padding-left: 6rem;
      padding-right: 6rem;
    }
    @media screen and (max-width: 991px) {
      :host {
        padding-top: 3rem;
      }
      .padding-global {
        padding-left: 1rem;
        padding-right: 1rem;
      }
      .lvc-search-results-text__wrapper {
        flex-direction: column;
        gap: 1rem;
      }
      lvc-share {
        margin-left: 0;
      }
    }
    @media screen and (max-width: 767px) {
      h2 {
        font-size: 26px;
      }
    }
  `
}
